import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import BookingDrawer from './BookingDrawer';
import SearchDrawer from './SearchDrawer';
import { HeartIcon, CartIcon } from './Icons';
import { triggerStockLimitAlert, resolveMediaUrl } from '../utils/firebaseService';

export default function Wishlist({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  cartItems = [],
  setCartItems,
  products = [],
  setSelectedProduct,
  currentUser,
  setCurrentUser,
  wishlist = [],
  toggleWishlist,
}) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [addedIds, setAddedIds] = useState({});

  // Filter products that exist in wishlist
  const wishlistedProducts = (products || []).filter(p => 
    wishlist.some(id => String(id) === String(p.id))
  );

  const handleAddToCart = (product) => {
    const liveProd = (products || []).find(p => String(p.id) === String(product.id)) || product;
    const maxStock = typeof liveProd.stock === 'number' ? liveProd.stock : 10;

    if (maxStock <= 0) {
      triggerStockLimitAlert(0);
      return;
    }

    let limitReached = false;
    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(product.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + 1 > maxStock) {
        limitReached = true;
        triggerStockLimitAlert(maxStock);
        return prev;
      }
      if (existing) {
        return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, stock: maxStock, qty: 1 }];
    });

    if (!limitReached) {
      setAddedIds(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedIds(prev => {
          const next = { ...prev };
          delete next[product.id];
          return next;
        });
      }, 2000);
    }
  };

  const handleAddAllToCart = () => {
    let anyAdded = false;
    setCartItems(prev => {
      let updated = [...prev];
      wishlistedProducts.forEach(prod => {
        const liveProd = (products || []).find(p => String(p.id) === String(prod.id)) || prod;
        const maxStock = typeof liveProd.stock === 'number' ? liveProd.stock : 10;
        if (maxStock > 0) {
          const existing = updated.find(item => String(item.id) === String(prod.id));
          const currentQty = existing ? existing.qty : 0;
          if (currentQty + 1 <= maxStock) {
            anyAdded = true;
            if (existing) {
              updated = updated.map(item => String(item.id) === String(prod.id) ? { ...item, qty: item.qty + 1 } : item);
            } else {
              updated.push({ ...prod, stock: maxStock, qty: 1 });
            }
          }
        }
      });
      return updated;
    });

    if (anyAdded) {
      setIsCartOpen(true);
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen flex flex-col font-sans text-[#584738] antialiased">
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* Main Content - Compact top padding brings items immediately into the front viewport */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-3.5 sm:px-6 md:px-12 pt-3 sm:pt-5 pb-12">
        {/* Streamlined Luxury Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 sm:pb-4 border-b border-[#584738]/10 mb-3.5 sm:mb-6">
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs tracking-widest uppercase font-light text-[#584738]/60 mb-1">
              <button 
                onClick={() => setView('home')} 
                className="hover:text-[#584738] transition-colors focus:outline-none cursor-pointer"
              >
                Home
              </button>
              <span>/</span>
              <span className="text-[#584738] font-medium">Wishlist</span>
            </div>

            {/* Title & Count */}
            <div className="flex items-baseline gap-2.5">
              <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-normal tracking-wide text-[#584738]">
                My Wishlist
              </h1>
              <span className="font-sans text-xs sm:text-sm text-[#B59E7D] font-medium tracking-normal">
                ({wishlistedProducts.length} {wishlistedProducts.length === 1 ? 'creation' : 'creations'})
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {wishlistedProducts.length > 0 && (
            <div className="flex items-center gap-2 sm:gap-3 self-stretch sm:self-auto">
              <button
                onClick={handleAddAllToCart}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 text-[10px] sm:text-xs font-sans font-semibold tracking-wider uppercase bg-[#584738] text-[#FAF8F5] px-4 py-2.5 hover:bg-black transition-all duration-300 shadow-xs cursor-pointer active:scale-95"
              >
                <CartIcon className="w-3.5 h-3.5" />
                <span>Move All To Bag</span>
              </button>
              <button
                onClick={() => setView('marketplace')}
                className="hidden md:inline-flex items-center gap-1.5 text-xs font-sans font-medium tracking-wider uppercase border border-[#584738]/30 px-3.5 py-2 hover:border-[#584738] text-[#584738] transition-colors cursor-pointer"
              >
                Explore More &rarr;
              </button>
            </div>
          )}
        </div>

        {/* Wishlist Items Grid or Empty State */}
        {wishlistedProducts.length === 0 ? (
          <div className="max-w-md mx-auto text-center py-12 sm:py-16 px-6 bg-white border border-[#584738]/10 rounded-xs shadow-xs my-4 sm:my-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-500">
              <HeartIcon filled={false} className="w-7 h-7 text-rose-500" />
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#584738] font-normal tracking-wide mb-2">
              Your Wishlist is Empty
            </h2>
            <p className="text-xs sm:text-sm text-[#584738]/70 font-light leading-relaxed mb-6">
              Explore our handcrafted Jaipur silver jewellery and tap the heart icon on any piece to save it to your private sanctuary.
            </p>
            <button
              onClick={() => setView('marketplace')}
              className="inline-flex items-center justify-center bg-[#584738] text-[#FAF8F5] px-7 py-3 text-xs font-sans tracking-[0.2em] uppercase font-medium hover:bg-black transition-all duration-300 shadow-sm active:scale-95 cursor-pointer"
            >
              Explore All Jewellery &rarr;
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-4 md:gap-6">
            {wishlistedProducts.map((product) => {
              const hasDiscount = product.discount > 0;
              const finalPrice = hasDiscount 
                ? product.price - (product.price * product.discount) / 100
                : product.price;
              const currentStock = typeof product.stock === 'number' ? product.stock : 10;
              const isOutOfStock = currentStock <= 0;

              return (
                <div 
                  key={product.id} 
                  className="bg-white border border-[#584738]/12 rounded-xs p-2.5 sm:p-3.5 flex flex-col justify-between group hover:shadow-lg transition-all duration-300 relative"
                >
                  {/* Card Top: Image + Badges */}
                  <div className="relative aspect-[1/1] sm:aspect-[4/5] w-full overflow-hidden rounded-xs bg-[#FAF8F5] mb-2 sm:mb-3">
                    {/* Discount Badge on Top-Left */}
                    {hasDiscount && (
                      <div className="absolute top-2 left-2 z-10 bg-[#8C3E3E] text-white text-[8.5px] sm:text-[9.5px] font-sans font-bold tracking-wider px-1.5 sm:px-2 py-0.5 uppercase shadow-xs rounded-xs">
                        {product.discount}% Off
                      </div>
                    )}

                    {/* Top-Right: Remove from Wishlist Heart Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (toggleWishlist) toggleWishlist(product.id);
                      }}
                      title="Remove from Wishlist"
                      aria-label="Remove from Wishlist"
                      className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-white/95 backdrop-blur-xs text-rose-600 border border-rose-200/80 hover:bg-rose-50 flex items-center justify-center shadow-xs transition-all duration-300 ease-out active:scale-90 hover:scale-110 cursor-pointer touch-manipulation"
                    >
                      <HeartIcon
                        filled={true}
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        activeColor="#E11D48"
                      />
                    </button>

                    {/* Out of Stock Strip on Bottom of Image */}
                    {isOutOfStock && (
                      <div className="absolute inset-x-0 bottom-0 py-1 bg-[#584738]/90 text-[#F5E6D8] text-[8.5px] sm:text-[9.5px] font-sans font-bold tracking-widest uppercase text-center backdrop-blur-xs z-10">
                        Out of Stock
                      </div>
                    )}

                    {/* Product Image */}
                    <div
                      onClick={() => { setSelectedProduct(product); setView('product'); }}
                      className="w-full h-full cursor-pointer flex items-center justify-center bg-[#FAF8F5]"
                    >
                      <img
                        src={resolveMediaUrl(product.image || product.images?.[0] || '')}
                        alt={product.name}
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.target.onerror = null;
                          const altImg = (product.images || []).map(resolveMediaUrl).find(u => u && u !== e.target.src);
                          if (altImg) {
                            e.target.src = altImg;
                          } else {
                            e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500" viewBox="0 0 400 500"><rect width="400" height="500" fill="%23F4EFEA"/><text x="50%" y="48%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="18" fill="%23584738" letter-spacing="2">HOUSE OF KARNNIKARAA</text><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="11" fill="%238C7D6E" letter-spacing="1">Handcrafted Heritage</text></svg>';
                          }
                        }}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isOutOfStock ? 'opacity-70 grayscale' : ''}`}
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Card Body: Category, Title, Price, Action */}
                  <div className="flex flex-col flex-1 justify-between text-center">
                    <div>
                      {/* Category */}
                      <div className="text-[8.5px] sm:text-[9.5px] font-sans tracking-[0.2em] uppercase text-[#B59E7D] font-medium mb-1 truncate">
                        {product.category || 'Silver Jewellery'}
                      </div>

                      {/* Product Title - Fixed Height so all cards align mathematically */}
                      <h4
                        onClick={() => { setSelectedProduct(product); setView('product'); }}
                        className="font-serif text-[#584738] text-[12px] sm:text-[14px] md:text-[15px] font-medium tracking-wide leading-snug h-[34px] sm:h-[38px] line-clamp-2 overflow-hidden flex items-center justify-center cursor-pointer hover:underline transition-colors"
                        title={product.name}
                      >
                        {product.name}
                      </h4>

                      {/* Price Row */}
                      <div className="h-[22px] flex items-center justify-center gap-1.5 sm:gap-2 my-1 sm:my-1.5">
                        {hasDiscount ? (
                          <>
                            <span className="font-sans text-[12px] sm:text-[13px] md:text-sm text-[#8C3E3E] font-semibold">
                              ₹{finalPrice.toLocaleString()}
                            </span>
                            <span className="font-sans text-[10px] sm:text-[11px] text-[#584738]/40 line-through">
                              ₹{product.price.toLocaleString()}
                            </span>
                          </>
                        ) : (
                          <span className="font-sans text-[12px] sm:text-[13px] md:text-sm text-[#584738] font-semibold">
                            ₹{product.price.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action: Move To Bag / Out of stock */}
                    <div className="mt-2">
                      {isOutOfStock ? (
                        <button
                          disabled={true}
                          className="w-full py-2 sm:py-2.5 text-[9px] sm:text-[10px] tracking-widest font-sans uppercase font-medium bg-neutral-100 text-neutral-400 border border-neutral-200 cursor-not-allowed rounded-xs"
                        >
                          Out of Stock
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={addedIds[product.id]}
                          className={`w-full py-2 sm:py-2.5 text-[9px] sm:text-[10px] tracking-widest font-sans uppercase font-semibold transition-all duration-300 rounded-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 shadow-xs ${
                            addedIds[product.id] 
                              ? 'bg-[#2D5A3A] text-white' 
                              : 'bg-[#584738] text-[#FAF8F5] hover:bg-black'
                          }`}
                        >
                          {addedIds[product.id] ? (
                            <>
                              <svg className="w-3.5 h-3.5 stroke-current animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Added to Bag</span>
                            </>
                          ) : (
                            <span>Move To Bag</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        setSelectedCollectionId={() => {}}
      />

      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        setSelectedCollectionId={() => {}}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthOpen={setIsAuthOpen}
        wishlist={wishlist}
      />

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
      />

      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => {
          setIsBookingOpen(false);
          if (setBookingStep) setBookingStep(1);
        }}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
      />

      <SearchDrawer
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        products={products}
        setCartItems={setCartItems}
        setSelectedProduct={setSelectedProduct}
        setView={setView}
        wishlist={wishlist}
        toggleWishlist={toggleWishlist}
      />
    </div>
  );
}
